import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState, lazy, Suspense } from 'react';
import { useStorageStore } from './store/useStorageStore';
import MainLayout from './components/MainLayout';
import BootstrapOnboarding from './components/BootstrapOnboarding';
import { isBootstrapReady, readBootstrapConfig } from './utils/bootstrapConfig';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const SourceScanner = lazy(() => import('./pages/mirror/SourceScanner'));
const TemplateFactory = lazy(() => import('./pages/mirror/TemplateFactory'));
const ElementSurgery = lazy(() => import('./pages/mirror/ElementSurgery'));
const SiteOverview = lazy(() => import('./pages/mirror/SiteOverview'));
const MenuEditor = lazy(() => import('./pages/mirror/MenuEditor'));
const PageStructureEditor = lazy(() => import('./pages/mirror/PageStructureEditor'));
const SiteMetadataEditor = lazy(() => import('./pages/mirror/SiteMetadataEditor'));
const PreviewCompare = lazy(() => import('./pages/mirror/PreviewCompare'));
const PublishCenter = lazy(() => import('./pages/mirror/PublishCenter'));
const VersionHistory = lazy(() => import('./pages/mirror/VersionHistory'));
const MigrationCenter = lazy(() => import('./pages/mirror/MigrationCenter'));
const ArticleEditor = lazy(() => import('./pages/content/ArticleEditor'));
const RouteManager = lazy(() => import('./pages/content/RouteManager'));
const AnalyticsHeatmap = lazy(() => import('./pages/analytics/Heatmap'));
const AnalyticsTimeline = lazy(() => import('./pages/analytics/Timeline'));
const GithubConfig = lazy(() => import('./pages/storage/GithubConfig'));
const TimeTravel = lazy(() => import('./pages/storage/TimeTravel'));

function RouteLoadingScreen() {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="text-center text-gray-600">
        <div className="mb-3 inline-block h-8 w-8 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600"></div>
        <p>Loading workspace...</p>
      </div>
    </div>
  );
}

function withRouteSuspense(node) {
  return <Suspense fallback={<RouteLoadingScreen />}>{node}</Suspense>;
}

function HomeEntryGate() {
  const config = readBootstrapConfig();
  if (!config?.domainCheckCompleted) {
    return <Navigate to="/mirror/source" replace />;
  }
  return withRouteSuspense(<Dashboard />);
}

function App() {
  const fetchStorageStatus = useStorageStore((state) => state.fetchStatus);
  const [bootstrapConfig, setBootstrapConfig] = useState(() => readBootstrapConfig());
  const [bootstrapReady, setBootstrapReady] = useState(() =>
    isBootstrapReady(readBootstrapConfig())
  );

  useEffect(() => {
    if (!bootstrapReady) return undefined;

    fetchStorageStatus();
    const interval = setInterval(fetchStorageStatus, 60000);
    return () => clearInterval(interval);
  }, [bootstrapReady, fetchStorageStatus]);

  const handleBootstrapComplete = (savedConfig) => {
    setBootstrapConfig(savedConfig);
    setBootstrapReady(true);
  };

  if (!bootstrapReady) {
    return (
      <BootstrapOnboarding
        initialConfig={bootstrapConfig}
        onComplete={handleBootstrapComplete}
      />
    );
  }

  return (
    <Routes>
      <Route path="/" element={<MainLayout />}>
        <Route index element={<HomeEntryGate />} />
        <Route path="dashboard" element={withRouteSuspense(<Dashboard />)} />
        <Route path="mirror">
          <Route path="source" element={withRouteSuspense(<SourceScanner />)} />
          <Route path="site" element={withRouteSuspense(<SiteOverview />)} />
          <Route path="factory" element={withRouteSuspense(<TemplateFactory />)} />
          <Route path="navigation" element={withRouteSuspense(<MenuEditor />)} />
          <Route path="structure" element={withRouteSuspense(<PageStructureEditor />)} />
          <Route path="metadata" element={withRouteSuspense(<SiteMetadataEditor />)} />
          <Route path="preview" element={withRouteSuspense(<PreviewCompare />)} />
          <Route path="publish" element={withRouteSuspense(<PublishCenter />)} />
          <Route path="history" element={withRouteSuspense(<VersionHistory />)} />
          <Route path="migration" element={withRouteSuspense(<MigrationCenter />)} />
          <Route path="editor" element={withRouteSuspense(<ElementSurgery />)} />
        </Route>
        <Route path="content">
          <Route path="publish" element={withRouteSuspense(<ArticleEditor />)} />
          <Route path="routes" element={withRouteSuspense(<RouteManager />)} />
        </Route>
        <Route path="analytics">
          <Route path="heatmap" element={withRouteSuspense(<AnalyticsHeatmap />)} />
          <Route path="timeline" element={withRouteSuspense(<AnalyticsTimeline />)} />
        </Route>
        <Route path="storage">
          <Route path="github" element={withRouteSuspense(<GithubConfig />)} />
          <Route path="history" element={withRouteSuspense(<TimeTravel />)} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default App;
